import { motion } from 'motion/react';

import { cn } from '@/shadcn/lib/utils';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { BatchJobSummary } from '@/hooks/useWorkflowBatchJobs';
import { formatNumber } from '@/utils/numberFormat';
import {
  modeLabel,
  outcomeChips,
  progressSegments
} from './batchJobPresentation';
import { BATCH_JOB_STATUS_META, visualStatusOf } from './batchJobStatusMeta';
import type { SubmitterIdentity } from './batchSubmitter';
import { BatchSubmitter } from './batchSubmitter';
import { modeTone } from './modeFilters';

type Props = {
  job: BatchJobSummary;
  selected: boolean;
  onSelect: (jobId: string) => void;
  submitter?: SubmitterIdentity;
  // Group label (the config key) -> the name people recognize.
  groupName?: string | null;
  // The icon this action declares in the workflow config, as drawn everywhere
  // else in the app. Absent for an action the config no longer describes.
  actionIcon?: string;
};

function timeOf(createdAt: string): string {
  return new Date(createdAt).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function BatchJobCard({
  job,
  selected,
  onSelect,
  submitter,
  groupName,
  actionIcon
}: Props) {
  const visual = visualStatusOf(job);
  const meta = BATCH_JOB_STATUS_META[visual];
  const title = job.actionTitle ?? job.actionKey;
  const running = visual === 'running';
  const { succeededPercent, failedPercent, processedPercent } =
    progressSegments(job);
  const chips = outcomeChips(job, !running);

  return (
    <motion.button
      animate={{ opacity: 1, y: 0 }}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border bg-card p-2.5 text-left',
        'hover:bg-card-hover',
        // ring-inset: an outer ring is clipped by the list's scroll container on
        // the edges, so the selected card looked half-outlined.
        selected
          ? 'border-primary ring-1 ring-primary ring-inset'
          : 'border-border'
      )}
      data-testid={`batch-job-card-${job.id}`}
      initial={{ opacity: 0, y: -8 }}
      layout
      onClick={() => onSelect(job.id)}
      transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
      type="button"
    >
      {/* The action's own icon, the same one the workflow draws for it, in the
          plain foreground: the status is already spelled out by the chips on the
          right, and tinting the action icon says it twice. */}
      <span
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground/5 text-foreground/70"
        data-icon={actionIcon}
        data-testid={`batch-job-card-icon-${job.id}`}
      >
        {actionIcon ? (
          <DynamicIcon icon={actionIcon} size={14} />
        ) : (
          <meta.Icon className={cn('size-3.5', running && 'animate-spin')} />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-semibold text-sm first-letter:uppercase">
            {title}
          </span>
          <span className="shrink-0 text-foreground/60 text-xs italic">
            {groupName ?? job.groupLabel}
          </span>
          {chips.length > 0 ? (
            chips.map(chip => (
              <span
                className={cn(
                  'shrink-0 rounded-full px-1.5 py-px font-semibold text-[10px]',
                  chip.tone === 'ok'
                    ? 'bg-green-600/15 text-green-600'
                    : 'bg-destructive/15 text-destructive'
                )}
                key={chip.key}
              >
                {chip.label}
              </span>
            ))
          ) : (
            <span
              className={cn(
                'shrink-0 rounded-full px-1.5 py-px font-semibold text-[10px]',
                meta.pill
              )}
            >
              {meta.label}
            </span>
          )}
        </span>

        <span className="mt-0.5 flex items-center gap-2 text-foreground/60 text-xs">
          <span>{timeOf(job.createdAt)}</span>
          <span aria-hidden>·</span>
          <span
            className={cn(
              'rounded px-1.5 py-px font-medium text-[10px]',
              modeTone(job.mode)
            )}
          >
            {modeLabel(job.mode)}
          </span>
          {job.runBy ? (
            <>
              <span aria-hidden>·</span>
              <BatchSubmitter
                identity={submitter ?? { email: job.runBy }}
                withBy
              />
            </>
          ) : null}
          {running ? (
            <span
              className="tabular-nums"
              data-testid="batch-job-card-progress"
            >
              {`${processedPercent}%`}
            </span>
          ) : null}
        </span>

        {/* A running job keeps a bar, because a percentage alone doesn't convey
            movement. A finished one doesn't: the outcome chips already say what
            happened, and a row of full bars is just noise down the list. */}
        {running ? (
          <span
            aria-hidden
            className="mt-1.5 flex h-1 w-full overflow-hidden rounded-full bg-foreground/10"
            data-testid={`batch-job-card-bar-${job.id}`}
          >
            <span
              className="h-full bg-primary"
              data-testid="batch-job-card-bar-succeeded"
              style={{ width: `${succeededPercent}%` }}
            />
            <span
              className="h-full bg-destructive"
              data-testid="batch-job-card-bar-failed"
              style={{ width: `${failedPercent}%` }}
            />
          </span>
        ) : null}
      </span>

      <span className="shrink-0 text-right">
        <span
          className="block font-bold text-sm tabular-nums"
          data-testid="batch-job-card-total"
        >
          {formatNumber(job.total)}
        </span>
        <span className="block text-[10px] text-foreground/60">traces</span>
      </span>
    </motion.button>
  );
}
